import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { AppShell } from './components/layout/AppShell';
import { ProtectedRoute } from './components/layout/ProtectedRoute';

// Pages
import LoginPage from './pages/auth/LoginPage';
import DashboardPage from './pages/DashboardPage';
import BaselinesPage from './pages/firmware/BaselinesPage';
import UploadBaselinePage from './pages/firmware/UploadBaselinePage';
import BaselineDetailPage from './pages/firmware/BaselineDetailPage';
import RolloutsPage from './pages/rollouts/RolloutsPage';
import RolloutDetailPage from './pages/rollouts/RolloutDetailPage';
import DeviceRegistryPage from './pages/devices/DeviceRegistryPage';
import DeviceDetailPage from './pages/devices/DeviceDetailPage';
import TelemetryPage from './pages/telemetry/TelemetryPage';
import UserManagementPage from './pages/admin/UserManagementPage';
import SegmentsPage from './pages/admin/SegmentsPage';
import AuditLogPage from './pages/admin/AuditLogPage';
import SystemConfigPage from './pages/admin/SystemConfigPage';
import LogTriagePage from './pages/inc2/LogTriagePage';

function ProtectedLayout({ children, allowedRoles }) {
  return (
    <ProtectedRoute allowedRoles={allowedRoles}>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AuthProvider>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<LoginPage />} />

            {/* Dashboard — all web roles */}
            <Route
              path="/"
              element={
                <ProtectedLayout allowedRoles={['FleetAdmin', 'ReleaseManager', 'SupportEngineer']}>
                  <DashboardPage />
                </ProtectedLayout>
              }
            />

            {/* Firmware — Admin + RM */}
            <Route
              path="/firmware"
              element={
                <ProtectedLayout allowedRoles={['FleetAdmin', 'ReleaseManager']}>
                  <BaselinesPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/firmware/upload"
              element={
                <ProtectedLayout allowedRoles={['FleetAdmin', 'ReleaseManager']}>
                  <UploadBaselinePage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/firmware/:id"
              element={
                <ProtectedLayout allowedRoles={['FleetAdmin', 'ReleaseManager']}>
                  <BaselineDetailPage />
                </ProtectedLayout>
              }
            />

            {/* Rollouts — all web roles */}
            <Route
              path="/rollouts"
              element={
                <ProtectedLayout allowedRoles={['FleetAdmin', 'ReleaseManager', 'SupportEngineer']}>
                  <RolloutsPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/rollouts/:id"
              element={
                <ProtectedLayout allowedRoles={['FleetAdmin', 'ReleaseManager', 'SupportEngineer']}>
                  <RolloutDetailPage />
                </ProtectedLayout>
              }
            />

            {/* Devices — Admin + Support + ReleaseManager */}
            <Route
              path="/devices"
              element={
                <ProtectedLayout allowedRoles={['FleetAdmin', 'SupportEngineer', 'ReleaseManager']}>
                  <DeviceRegistryPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/devices/:serial"
              element={
                <ProtectedLayout allowedRoles={['FleetAdmin', 'SupportEngineer', 'ReleaseManager']}>
                  <DeviceDetailPage />
                </ProtectedLayout>
              }
            />

            {/* Telemetry — all web roles */}
            <Route
              path="/telemetry"
              element={
                <ProtectedLayout allowedRoles={['FleetAdmin', 'ReleaseManager', 'SupportEngineer']}>
                  <TelemetryPage />
                </ProtectedLayout>
              }
            />

            {/* Admin — Admin only (except audit log also for Support) */}
            <Route
              path="/admin/users"
              element={
                <ProtectedLayout allowedRoles={['FleetAdmin']}>
                  <UserManagementPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/admin/segments"
              element={
                <ProtectedLayout allowedRoles={['FleetAdmin']}>
                  <SegmentsPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/admin/audit"
              element={
                <ProtectedLayout allowedRoles={['FleetAdmin', 'SupportEngineer']}>
                  <AuditLogPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/admin/config"
              element={
                <ProtectedLayout allowedRoles={['FleetAdmin']}>
                  <SystemConfigPage />
                </ProtectedLayout>
              }
            />

            {/* INC 2 stub — all web roles can preview */}
            <Route
              path="/inc2/logs"
              element={
                <ProtectedLayout allowedRoles={['FleetAdmin', 'ReleaseManager', 'SupportEngineer']}>
                  <LogTriagePage />
                </ProtectedLayout>
              }
            />

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}

